import * as React from 'react';

import { useSelector } from 'react-redux';

import { translate } from '~l10n/client';
import { PillButton } from '~shared/components/buttons';
import { AlignToStandards } from '~teacher/components/QuizEditor/QuizEditorHeader/PersonalQuizEditorHeader/AlignToStandards';
import { QuizEditorEditableQuizTitle } from '~teacher/components/QuizEditor/QuizEditorHeader/QuizEditorEditableQuizTitle';
import { ShareQuiz } from '~teacher/components/QuizEditor/QuizEditorHeader/ShareQuiz';
import { QuizEditorIsUploadingImageContext } from '~teacher/components/QuizEditor/QuizEditorIsUploadingImage';
import { PersonalQuizzesBreadcrumbs } from '~teacher/components/quizzes/PersonalQuizzesBreadcrumbs/PersonalQuizzesBreadcrumbs';
import { useQuizzes } from '~teacher/hook';
import { Quiz } from '~teacher/models/quizzes';
import { selectEditingQuiz } from '~teacher/slices/quiz/quizSelectors';

interface Props {
  isControlsDisabled: boolean;
  onHandleSaveAndExit: () => Promise<void>;
  editQuizController: any;
}
export function PersonalQuizEditorHeader({
  isControlsDisabled,
  editQuizController,
  onHandleSaveAndExit,
}: Props) {
  const quizzesQuery = useQuizzes();
  const quiz = useSelector(selectEditingQuiz) as Quiz & { isDirty: boolean };
  const { standard } = quiz;

  const standardId = standard?.id ?? null;

  const [showStandardDeleteModal, setShowDeleteStandardModal] =
    React.useState(false);

  const handleAlignQuizToStandardToggle = (checked: boolean) => {
    if (standardId) {
      setShowDeleteStandardModal(true);
    }
    editQuizController.alignToStandardChanged(checked);
    setIsAlignToStandardSectionOpen(checked);
  };

  const [isAlignToStandardSectionOpen, setIsAlignToStandardSectionOpen] =
    React.useState(!!standardId);

  React.useEffect(() => {
    const isAlignToStandardsSectionOpen = standardId !== null;
    setIsAlignToStandardSectionOpen(isAlignToStandardsSectionOpen);
  }, [standardId, setIsAlignToStandardSectionOpen]);

  return (
    <>
      <PersonalQuizzesBreadcrumbs
        currentItem={{ ...quiz, uniqueId: quiz.socNumber, type: 'quiz' }}
        folders={quizzesQuery.data?.itemById}
        hideCurrent
      />
      <div
        className="flex flex-col-reverse md:flex-row md:items-start md:justify-between py-12"
        data-quiz-title
      >
        <QuizEditorEditableQuizTitle isControlsDisabled={isControlsDisabled} />
        <QuizEditorIsUploadingImageContext.Consumer>
          {({ isUploadingImages }) => (
            <div>
              <PillButton
                className="flex-shrink-0 md:self-start self-end mb-8 md:mb-0 ml-4 md:ml-8"
                onClick={onHandleSaveAndExit}
                disabled={isUploadingImages || isControlsDisabled}
              >
                {translate('Save and Exit')}
              </PillButton>
            </div>
          )}
        </QuizEditorIsUploadingImageContext.Consumer>
      </div>
      <div
        className="flex flex-col sm:flex-row justify-end py-8 sm:py-3 shadow-border-t"
        data-quiz-settings
      >
        <ShareQuiz isControlsDisabled={isControlsDisabled} />
      </div>
      <AlignToStandards
        showStandardDeleteModal={showStandardDeleteModal}
        setShowDeleteStandardModal={setShowDeleteStandardModal}
        isAlignToStandardSectionOpen={isAlignToStandardSectionOpen}
        handleAlignQuizToStandardToggle={handleAlignQuizToStandardToggle}
        editQuizController={editQuizController}
        isControlsDisabled={isControlsDisabled}
      />
    </>
  );
}
